import { Component } from '@theme/component';

/**
 * <custom-select-component>
 *
 * Accessible custom select replacement. Trigger button mimics the
 * input field; options are rendered in a listbox panel below. Value
 * is written to a hidden <input> so the component participates in
 * native form submission alongside the rest of the SA Inputs system.
 *
 * Composed from snippets/custom-select.liquid.
 *
 * Markup contract (refs):
 *   - trigger      <button> the visible field
 *   - value        <span> inside trigger; rendered selection or placeholder
 *   - listbox      <ul role="listbox">
 *   - hiddenInput  <input type="hidden"> name + value for form submission
 *
 * Public API:
 *   - toggle() / open() / close()
 *   - selectByValue(value)            programmatic selection
 *   - get value() / get selectedOption()
 *
 * Events fired on the host element (bubbles):
 *   - custom-select:change            { value, label }
 *   - input + change on hiddenInput   for native form listeners
 *
 * Accessibility:
 *   - WAI-ARIA listbox pattern (button + listbox; not a true combobox
 *     since there's no text input)
 *   - Keyboard: Up/Down/Home/End navigate, Enter/Space select,
 *     Escape close + return focus, Tab closes and continues
 *   - Focus moves to selected option (or first) on open;
 *     returns to trigger on close
 *   - aria-activedescendant tracks the highlighted option
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} value
 * @property {HTMLElement} listbox
 * @property {HTMLInputElement} hiddenInput
 *
 * @extends Component<Refs>
 */
export class CustomSelectComponent extends Component {
  requiredRefs = ['trigger', 'value', 'listbox', 'hiddenInput'];

  /** @type {boolean} */
  #isOpen = false;

  /** @type {HTMLElement | null} */
  #activeOption = null;

  connectedCallback() {
    super.connectedCallback();

    // Cache options once on connect; mutation observer in the base
    // Component class keeps refs fresh if the listbox content changes.
    this.#bindOptionListeners();

    // Sync trigger label with the initial value (or placeholder).
    this.#syncTriggerLabel();

    // Outside-click and Escape are bound only while the panel is open.
    // Trigger keydown stays bound for the lifetime of the component
    // because it also handles open-from-closed via Down/Up arrows.
    this.refs.trigger.addEventListener('keydown', this.#onTriggerKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.trigger.removeEventListener('keydown', this.#onTriggerKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
  }

  // ----- Public API ---------------------------------------------------

  toggle = () => {
    if (this.#isDisabled) return;
    this.#isOpen ? this.close() : this.open();
  };

  open = () => {
    if (this.#isDisabled || this.#isOpen) return;
    this.#isOpen = true;
    this.refs.listbox.removeAttribute('aria-hidden');
    this.refs.listbox.dataset.open = 'true';
    this.refs.trigger.setAttribute('aria-expanded', 'true');

    // Highlight the currently-selected option (or the first enabled one).
    const selected = this.#options.find((o) => o.getAttribute('aria-selected') === 'true');
    const initial = selected ?? this.#options.find((o) => !this.#isOptionDisabled(o));
    if (initial) this.#highlight(initial);

    // Bind global listeners while open.
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);
  };

  close = ({ returnFocus = true } = {}) => {
    if (!this.#isOpen) return;
    this.#isOpen = false;
    this.refs.listbox.setAttribute('aria-hidden', 'true');
    delete this.refs.listbox.dataset.open;
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.refs.trigger.removeAttribute('aria-activedescendant');

    if (this.#activeOption) {
      delete this.#activeOption.dataset.active;
      this.#activeOption = null;
    }

    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);

    if (returnFocus) this.refs.trigger.focus();
  };

  /**
   * Programmatically select an option by its value. No-op if the value
   * doesn't match any option, or the matching option is disabled.
   *
   * @param {string} value
   */
  selectByValue = (value) => {
    const target = this.#options.find((o) => o.dataset.value === String(value));
    if (!target || this.#isOptionDisabled(target)) return;
    this.#commitSelection(target);
  };

  get value() {
    return this.refs.hiddenInput.value;
  }

  get selectedOption() {
    return this.#options.find((o) => o.getAttribute('aria-selected') === 'true') ?? null;
  }

  // ----- Internals ----------------------------------------------------

  get #options() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.refs.listbox.querySelectorAll('[role="option"]'))
    );
  }

  get #isDisabled() {
    return this.hasAttribute('disabled') || this.refs.trigger.disabled;
  }

  #isOptionDisabled = (/** @type {HTMLElement} */ option) =>
    option.getAttribute('aria-disabled') === 'true' || option.dataset.disabled === 'true';

  #bindOptionListeners = () => {
    for (const option of this.#options) {
      option.addEventListener('click', this.#onOptionClick);
      option.addEventListener('mousemove', this.#onOptionMousemove);
    }
  };

  #syncTriggerLabel = () => {
    const selected = this.selectedOption;
    if (selected) {
      this.refs.value.textContent = selected.dataset.label ?? selected.textContent?.trim() ?? '';
      this.refs.value.dataset.placeholder = 'false';
    } else {
      this.refs.value.textContent = this.dataset.placeholder ?? '';
      this.refs.value.dataset.placeholder = 'true';
    }
  };

  #highlight = (/** @type {HTMLElement} */ option) => {
    if (this.#activeOption) delete this.#activeOption.dataset.active;
    this.#activeOption = option;
    option.dataset.active = 'true';
    if (option.id) this.refs.trigger.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  };

  #commitSelection = (/** @type {HTMLElement} */ option) => {
    const value = option.dataset.value ?? '';
    const label = option.dataset.label ?? option.textContent?.trim() ?? '';

    for (const opt of this.#options) {
      opt.setAttribute('aria-selected', opt === option ? 'true' : 'false');
    }

    this.refs.hiddenInput.value = value;
    this.#syncTriggerLabel();

    // Native form events so existing listeners (validation, change
    // handlers) just work.
    this.refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.refs.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

    // Theme-level event for any custom integration.
    this.dispatchEvent(
      new CustomEvent('custom-select:change', {
        bubbles: true,
        detail: { value, label },
      })
    );

    this.close();
  };

  // ----- Event handlers ----------------------------------------------

  #onOptionClick = (/** @type {MouseEvent} */ event) => {
    const option = /** @type {HTMLElement} */ (event.currentTarget);
    if (this.#isOptionDisabled(option)) return;
    this.#commitSelection(option);
  };

  #onOptionMousemove = (/** @type {MouseEvent} */ event) => {
    const option = /** @type {HTMLElement} */ (event.currentTarget);
    if (this.#isOptionDisabled(option)) return;
    if (this.#activeOption !== option) this.#highlight(option);
  };

  #onTriggerKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (this.#isDisabled) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.#isOpen) this.open();
        break;
      case 'Home':
      case 'End':
        if (this.#isOpen) {
          event.preventDefault();
          const target =
            event.key === 'Home' ? this.#firstEnabledOption() : this.#lastEnabledOption();
          if (target) this.#highlight(target);
        }
        break;
    }
  };

  #onDocumentKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (!this.#isOpen) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        // Let Tab fall through naturally — close, then the browser
        // moves focus to the next focusable element.
        this.close({ returnFocus: false });
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.#move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#move(-1);
        break;
      case 'Home':
        event.preventDefault();
        {
          const target = this.#firstEnabledOption();
          if (target) this.#highlight(target);
        }
        break;
      case 'End':
        event.preventDefault();
        {
          const target = this.#lastEnabledOption();
          if (target) this.#highlight(target);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.#activeOption) this.#commitSelection(this.#activeOption);
        break;
    }
  };

  #onDocumentClick = (/** @type {MouseEvent} */ event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.close({ returnFocus: false });
  };

  #move = (/** @type {1 | -1} */ step) => {
    const opts = this.#options.filter((o) => !this.#isOptionDisabled(o));
    if (opts.length === 0) return;
    const currentIndex = this.#activeOption ? opts.indexOf(this.#activeOption) : -1;
    let next = currentIndex + step;
    if (next < 0) next = opts.length - 1;
    if (next >= opts.length) next = 0;
    this.#highlight(opts[next]);
  };

  #firstEnabledOption = () => this.#options.find((o) => !this.#isOptionDisabled(o)) ?? null;
  #lastEnabledOption = () => {
    const opts = this.#options.filter((o) => !this.#isOptionDisabled(o));
    return opts[opts.length - 1] ?? null;
  };
}

if (!customElements.get('custom-select-component')) {
  customElements.define('custom-select-component', CustomSelectComponent);
}
